import { DateFormatType, OrderStatusFilter } from '@/Enums';
import { Nullable, OrderFilterModel, OrderTableRow } from '@/Types';
import { orderMapping } from '@/Mapping/OrderMapping';
import { DateRangeParamType } from '@/Api';
import moment from 'moment/moment';
import { dateTimeUtils } from '@/Utils';
import { OrderModel } from '@/Models/Order/OrderModel';

type UpdatedOrderTableRowsArgs = {
  selectedOrder: Nullable<OrderTableRow>;
  selectedOrders: OrderTableRow[];
  orderRows: OrderTableRow[];
};

const updateOrderTableRows = (
  { selectedOrder, selectedOrders, orderRows }: UpdatedOrderTableRowsArgs,
  updatedOrders: OrderTableRow[],
): UpdatedOrderTableRowsArgs => {
  const resultOrderRows: OrderTableRow[] = orderRows;

  updatedOrders.forEach((sale) => {
    const orderIdx = resultOrderRows.findIndex((o) => o.id === sale.id);

    if (orderIdx != -1) {
      resultOrderRows[orderIdx] = sale;
    } else {
      resultOrderRows.push(sale);
    }
  });

  const resultSelectedOrder = updatedOrders.find((o) => o.id === selectedOrder?.id);

  const resultSelectedOrders = selectedOrders.map((sale) => {
    const order = updatedOrders.find((o) => o.id === sale.id);
    return order || sale;
  });

  return {
    orderRows: resultOrderRows,
    selectedOrder: resultSelectedOrder || null,
    selectedOrders: resultSelectedOrders,
  };
};

const handleOrderCreation = (state: any, orderModel: OrderModel) => {
  const orderDate = dateTimeUtils.utcStringToLocalString(orderModel.mpCreatedTime);

  const filterDateStart = moment(orderDate, DateFormatType.LOCAL_DATE_TIME)
    .startOf('day')
    .format(DateFormatType.LOCAL_DATE_TIME);
  const filterDateEnd = moment(orderDate, DateFormatType.LOCAL_DATE_TIME)
    .endOf('day')
    .format(DateFormatType.LOCAL_DATE_TIME);

  state.orderFilters = {
    ...state.orderFilters,
    orderStatusFilter: OrderStatusFilter.All,
    dateRange: {
      ...state?.orderFilters?.dateRange,
      timeFrom: filterDateStart,
      timeTo: filterDateEnd,
    },
  } as OrderFilterModel;

  state.filterUpdate = {
    newOrderData: orderModel,
    storeFilterChanged: true,
  };
  state.modals.orderFormModalData = { visible: false };
};

const handleOrdersUpdate = (state: any, updatedOrders: OrderTableRow[]) => {
  if (updatedOrders.length == 0) return;

  const { selectedOrder, orderRows, selectedOrders } = salesUtils.updateOrderTableRows(
    {
      selectedOrder: state.selectedSale,
      orderRows: state.sales,
      selectedOrders: state.selectedSales,
    },
    updatedOrders,
  );

  state.salesDataSource?.update(
    updatedOrders.map((row) => ({
      key: row.id,
      data: { ...row },
    })),
  );

  state.sales = orderRows;
  state.selectedSale = selectedOrder;
  state.selectedSales = selectedOrders;
};

function getDefaultFilters(mpAccountIds?: number[]): OrderFilterModel {
  return {
    mpAccountIds: mpAccountIds ?? [],
    orderStatusFilter: OrderStatusFilter.NotShippedAll,
    dateRange: {
      rangeType: DateRangeParamType.CreationTime,
      timeFrom: moment().subtract(6, 'days').startOf('day').format(DateFormatType.LOCAL_DATE_TIME),
      timeTo: moment().endOf('day').format(DateFormatType.LOCAL_DATE_TIME),
    },
    searchString: '',
    searchGroup: '',
  };
}

const getIsFilterChanged = (
  initialFilters: OrderFilterModel,
  newFilters: OrderFilterModel & { dateRangeTuple: string },
  needSearch?: boolean,
) => {
  // Date equality
  const timeFrom = newFilters.dateRangeTuple?.split(' - ')[0];
  const timeTo = newFilters.dateRangeTuple?.split(' - ')[1];
  const isDateRangeEqual = initialFilters.dateRange.timeFrom === timeFrom && initialFilters.dateRange.timeTo === timeTo;

  // Mp account equality
  let isMpAccountIdsEqual =
    initialFilters.mpAccountIds?.length == 0 || initialFilters.mpAccountIds?.length == newFilters.mpAccountIds?.length;

  if (isMpAccountIdsEqual && initialFilters.mpAccountIds?.length != 0) {
    for (let i = 0; i < (newFilters.mpAccountIds?.length ?? 0); i++) {
      isMpAccountIdsEqual = initialFilters.mpAccountIds?.[i] == newFilters.mpAccountIds?.[i];
      if (!isMpAccountIdsEqual) break;
    } // for
  } // if

  // Status equality
  const isOrderStatusEqual = initialFilters.orderStatusFilter === newFilters.orderStatusFilter;

  if (!needSearch) {
    return !isDateRangeEqual || !isMpAccountIdsEqual || !isOrderStatusEqual;
  }

  // Search equality
  const isSearchTextEqual = initialFilters.searchString == newFilters.searchString;

  // Group equality
  const isSearchGroupEqual = initialFilters.searchGroup == newFilters.searchGroup;

  return !isDateRangeEqual || !isMpAccountIdsEqual || !isOrderStatusEqual || !isSearchTextEqual || !isSearchGroupEqual;
};

const convertOrderPreviewData = (order: OrderModel) => {
  const previewData = orderMapping.convertOrderToOrderTableRow(order);

  return {
    ...previewData,
    paymentDueDate: dateTimeUtils.changeDateFormat(
      previewData?.paymentDueDate,
      DateFormatType.SERVER_DATE_TIME,
      DateFormatType.LOCAL_DATE,
    ),
  };
};

export const salesUtils = {
  updateOrderTableRows,
  getDefaultFilters,
  getIsFilterChanged,
  handleOrderCreation,
  handleOrdersUpdate,
  convertOrderPreviewData,
};
